import AppIconButton from "./form/AppIconButton";
import { Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppActionsRow, AppFormField, AppFormGrid, PageSectionCard } from "./layout";
import { AppAutocomplete, AppButton, AppTextField } from "./form";
import { GRID_FULL, GRID_HALF, formRowButtonSx } from "../lib/ui/formLayout";
import type { WidgetParameter } from "../types/widget";
import { useAppCustomStyles } from "../assets/theme/custom/hooks";

const BIND_OPTIONS = ["dashboard.dateFrom", "dashboard.dateTo"] as const;

const TYPE_OPTIONS = ["datetime", "string", "number", "boolean"] as const;

type BindOption = { value: string; label: string };
type TypeOption = { value: string; label: string };
type RequiredOption = { value: string; label: string };

type Props = {
  parameters: WidgetParameter[];
  onChange: (parameters: WidgetParameter[]) => void;
  /** Narrow layout for chat draft panel — prevents field overflow. */
  compact?: boolean;
  /** When true, show adaptor-oriented placeholder help ({{from}}/{{to}} in SQL/Mongo). */
  adaptorSource?: boolean;
};

function emptyParameter(): WidgetParameter {
  return { name: "", type: "string", bind: "", required: false };
}

export default function WidgetParametersEditor({
  parameters,
  onChange,
  compact = false,
  adaptorSource = false,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;

  const typeOptions: TypeOption[] = useMemo(
    () => TYPE_OPTIONS.map((v) => ({ value: v, label: v })),
    []
  );

  const bindOptions: BindOption[] = useMemo(
    () => [
      { value: "", label: t("widget.paramBindNone") },
      ...BIND_OPTIONS.map((v) => ({ value: v, label: v })),
      { value: "__constant__", label: t("widget.paramBindConstant") },
    ],
    [t]
  );

  const requiredOptions: RequiredOption[] = useMemo(
    () => [
      { value: "yes", label: t("common.yes") },
      { value: "no", label: t("common.no") },
    ],
    [t]
  );

  const update = (index: number, patch: Partial<WidgetParameter>) => {
    onChange(parameters.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const remove = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...parameters, emptyParameter()]);
  };

  const bindValueFor = (param: WidgetParameter) =>
    param.bind?.startsWith("constant.") ? "__constant__" : param.bind || "";

  return (
    <Stack spacing={compact ? 1 : 1.5} sx={styles.parametersStack}>
      <Typography variant="body2" color="text.secondary">
        {adaptorSource ? t("widget.parametersHelpAdaptor") : t("widget.parametersHelp")}
      </Typography>
      <AppActionsRow>
        <AppButton
          size="small"
          variant="outlined"
          startIcon={<AddIcon fontSize={compact ? "small" : "medium"} />}
          onClick={add}
          sx={styles.parametersAddButton(compact)}
        >
          {t("widget.addParameter")}
        </AppButton>
      </AppActionsRow>

      {parameters.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={styles.parametersEmpty(compact)}>
          {t("widget.parametersEmpty")}
        </Typography>
      )}

      {parameters.map((param, index) => (
        <PageSectionCard key={`param-${index}`} noPadding>
          <AppFormGrid spacing={compact ? "compact" : "default"} alignItems="flex-end" sx={styles.parametersCardGrid(compact)}>
            <AppFormField size={compact ? GRID_HALF : { xs: 12, lg: 3 }}>
              <AppTextField
                label={t("widget.paramName")}
                value={param.name}
                onChange={(e) => update(index, { name: e.target.value })}
                size={compact ? "small" : undefined}
              />
            </AppFormField>
            <AppFormField size={compact ? GRID_HALF : { xs: 12, lg: 2 }}>
              <AppAutocomplete<TypeOption>
                label={t("widget.paramType")}
                options={typeOptions}
                value={typeOptions.find((o) => o.value === (param.type || "string")) ?? typeOptions[1]}
                onChange={(_, opt) => opt && update(index, { type: opt.value })}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                size={compact ? "small" : undefined}
              />
            </AppFormField>
            <AppFormField size={compact ? GRID_FULL : { xs: 12, lg: 3 }}>
              <AppAutocomplete<BindOption>
                label={t("widget.paramBind")}
                options={bindOptions}
                value={bindOptions.find((o) => o.value === bindValueFor(param)) ?? bindOptions[0]}
                onChange={(_, opt) => {
                  if (!opt) return;
                  if (opt.value === "__constant__") {
                    update(index, { bind: `constant.${param.name || "value"}` });
                  } else {
                    update(index, { bind: opt.value });
                  }
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                size={compact ? "small" : undefined}
              />
            </AppFormField>
            {param.bind?.startsWith("constant.") && (
              <AppFormField size={compact ? GRID_FULL : { xs: 12, lg: 2 }}>
                <AppTextField
                  label={t("widget.paramDefault")}
                  value={String(param.defaultValue ?? "")}
                  onChange={(e) => update(index, { defaultValue: e.target.value })}
                  size={compact ? "small" : undefined}
                />
              </AppFormField>
            )}
            <AppFormField size={compact ? GRID_HALF : { xs: 12, lg: 2 }}>
              <AppAutocomplete<RequiredOption>
                label={t("widget.paramRequired")}
                options={requiredOptions}
                value={
                  requiredOptions.find((o) => o.value === (param.required ? "yes" : "no")) ??
                  requiredOptions[1]
                }
                onChange={(_, opt) => opt && update(index, { required: opt.value === "yes" })}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                size={compact ? "small" : undefined}
              />
            </AppFormField>
            <AppFormField size={compact ? GRID_HALF : { xs: 12, lg: "auto" }}>
              <AppIconButton
                size={compact ? "small" : "medium"}
                color="error"
                confirmDelete={{ itemName: param.name || String(index + 1) }}
                onClick={() => remove(index)}
                aria-label={t("common.delete")}
                sx={styles.parametersDeleteButton(compact)}
              >
                <DeleteOutlineIcon fontSize={compact ? "small" : "medium"} />
              </AppIconButton>
            </AppFormField>
          </AppFormGrid>
        </PageSectionCard>
      ))}
    </Stack>
  );
}
